import { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'antd';
import type { FormInstance } from 'antd';
import { AddProductsModal } from '../../pages/Looks/LookItem/AddProductsModal';
import type { LookDTOType } from '../../schemas/styling';
import {
  LOOK_CONTENT_EDITOR_ENRICHMENT_SECTIONS,
  LOOK_CONTENT_EDITOR_METADATA_FIXED_ORDER,
  LOOK_CONTENT_EDITOR_STANDARD_ORDER,
} from './constants';
import EnrichmentSectionDialog from './EnrichmentSectionDialog';
import { normalizeEnrichmentPayload } from './GeneratedWidgetList';
import { normalizeRecommendations } from './helpers';
import { ProductRecommendationsSection } from './ProductRecommendationsSection';
import LookNameSection from './sections/LookNameSection';
import OccasionSection from './sections/OccasionSection';
import PdpContentSection from './sections/PdpContentSection';
import SearchSeedsSection from './sections/SearchSeedsSection';
import VisualSearchSection from './sections/VisualSearchSection';
import WebsiteContentSection from './sections/WebsiteContentSection';
import type {
  LookContentEditorSection,
  LookContentFormValues,
  ProductRecommendationItem,
} from './types';

interface LookContentEditorProps {
  form: FormInstance<LookContentFormValues>;
  mode?: 'compact' | 'full';
  presentation?: 'timeline' | 'flat';
  showMetadata?: boolean;
  section?: LookContentEditorSection;
  sections?: Array<LookContentEditorSection>;
  initialProductRecommendations?: LookContentFormValues['product_recommendations'];
  initialSearchSeeds?: LookContentFormValues['search_seeds'];
  baseProductColor?: string;
  productPickerPortalContainer?: HTMLElement | null;
  widgets?: unknown;
  visualSearch?: unknown;
  lookId?: number;
  isPublished?: boolean;
  isTriggeringVisualSearch?: boolean;
  onTriggerVisualSearch?: () => void;
  onRefreshVisualSearch?: () => void;
  onVisualSearchUpdated?: (look: LookDTOType) => void;
  onSearchSeedsUpdated?: (look: LookDTOType) => void;
  scrollContainer?: HTMLElement | null;
  compactEnrichments?: boolean;
  onSaveEnrichmentSection?: (
    section: LookContentEditorSection,
  ) => Promise<boolean | void> | boolean | void;
  onResetEnrichmentSection?: (section: LookContentEditorSection) => void;
  isSavingEnrichmentSection?: boolean;
}

const LookContentEditor = ({
  form,
  mode = 'compact',
  presentation = 'timeline',
  showMetadata = false,
  section,
  sections,
  initialProductRecommendations,
  initialSearchSeeds,
  baseProductColor,
  productPickerPortalContainer,
  widgets,
  visualSearch,
  lookId,
  isPublished = false,
  isTriggeringVisualSearch = false,
  onTriggerVisualSearch,
  onRefreshVisualSearch,
  onVisualSearchUpdated,
  onSearchSeedsUpdated,
  scrollContainer,
  compactEnrichments = false,
  onSaveEnrichmentSection,
  onResetEnrichmentSection,
  isSavingEnrichmentSection = false,
}: LookContentEditorProps) => {
  const isSectionMode = Boolean(section);
  const orderedSections: Array<LookContentEditorSection> = section
    ? [section]
    : sections && sections.length > 0
      ? sections
      : showMetadata
        ? [...LOOK_CONTENT_EDITOR_METADATA_FIXED_ORDER, ...LOOK_CONTENT_EDITOR_ENRICHMENT_SECTIONS]
        : mode === 'full'
          ? LOOK_CONTENT_EDITOR_STANDARD_ORDER
          : ['occasion', 'pdp'];
  const sectionRefs = useRef<Partial<Record<LookContentEditorSection, HTMLElement | null>>>({});
  const [activeSection, setActiveSection] = useState<LookContentEditorSection>(orderedSections[0]);

  const occasionName = Form.useWatch('occasion_name', form);
  const subOccasionName = Form.useWatch('sub_occasion_name', form);
  const lookName = Form.useWatch('look_name', form);
  const lookHook = Form.useWatch('look_hook', form);
  const stylistNote = Form.useWatch(['metadata', 'website_content', 'stylist_note'], form);
  const blurb = Form.useWatch('blurb', form);
  const description = Form.useWatch('description', form);
  const sceneDescription = Form.useWatch('scene_description', form);
  const watchedSearchSeeds = Form.useWatch('search_seeds', form);

  const [recommendationPickerState, setRecommendationPickerState] = useState<{
    open: boolean;
    recommendationIndex: number | null;
    productType: string;
    mode: 'recommendations' | 'selection';
  }>({
    open: false,
    recommendationIndex: null,
    productType: '',
    mode: 'selection',
  });
  const [activeEnrichmentSection, setActiveEnrichmentSection] =
    useState<LookContentEditorSection | null>(null);
  const searchSeedItems = useMemo(() => {
    const formSearchSeeds =
      watchedSearchSeeds ??
      (form.getFieldValue('search_seeds') as LookContentFormValues['search_seeds'] | undefined) ??
      initialSearchSeeds;

    return Array.isArray(formSearchSeeds) ? formSearchSeeds : [];
  }, [form, initialSearchSeeds, watchedSearchSeeds]);

  const openRecommendationProductPicker = (
    recommendationIndex: number,
    productType: string,
    mode: 'recommendations' | 'selection' = 'selection',
  ) => {
    setRecommendationPickerState({
      open: true,
      recommendationIndex,
      productType,
      mode,
    });
  };

  const closeRecommendationProductPicker = () => {
    setRecommendationPickerState({
      open: false,
      recommendationIndex: null,
      productType: '',
      mode: 'selection',
    });
  };

  const [recommendations, setRecommendations] = useState<Array<ProductRecommendationItem>>(() =>
    normalizeRecommendations(
      ((form as unknown as FormInstance<Record<string, unknown>>).getFieldValue(
        'product_recommendations',
      ) as LookContentFormValues['product_recommendations'] | undefined) ??
        initialProductRecommendations,
    ),
  );

  useEffect(() => {
    const formRecommendations = (
      form as unknown as FormInstance<Record<string, unknown>>
    ).getFieldValue('product_recommendations')
      ? ((form as unknown as FormInstance<Record<string, unknown>>).getFieldValue(
          'product_recommendations',
        ) as LookContentFormValues['product_recommendations'])
      : undefined;

    setRecommendations(
      normalizeRecommendations(formRecommendations ?? initialProductRecommendations),
    );
  }, [form, initialProductRecommendations]);

  const updateRecommendation = (
    recommendationIndex: number,
    updater: (current: ProductRecommendationItem) => ProductRecommendationItem,
  ) => {
    const nextRecommendations = recommendations.map((recommendation, index) =>
      index === recommendationIndex ? updater(recommendation) : recommendation,
    );
    setRecommendations(nextRecommendations);
    form.setFieldValue('product_recommendations', nextRecommendations);
  };

  const addRecommendation = () => {
    const nextRecommendations = [
      ...recommendations,
      {
        product_type: '',
        description: '',
        mapped_lk_styles: [],
        mapped_product_images: {},
      },
    ];
    setRecommendations(nextRecommendations);
    form.setFieldValue('product_recommendations', nextRecommendations);
  };

  const removeRecommendation = (recommendationIndex: number) => {
    const nextRecommendations = recommendations.filter((_, index) => index !== recommendationIndex);
    setRecommendations(nextRecommendations);
    form.setFieldValue('product_recommendations', nextRecommendations);
  };

  const removeMappedProduct = async (recommendationIndex: number, productId: string) => {
    const nextRecommendations = recommendations.map((recommendation, index) => {
      if (index !== recommendationIndex) {
        return recommendation;
      }

      return {
        ...recommendation,
        mapped_lk_styles: (recommendation.mapped_lk_styles || []).filter(
          value => String(value) !== productId,
        ),
        mapped_product_images: Object.fromEntries(
          Object.entries(recommendation.mapped_product_images || {}).filter(
            ([key]) => key !== productId,
          ),
        ),
      };
    });

    setRecommendations(nextRecommendations);
    form.setFieldValue('product_recommendations', nextRecommendations);

    if (onSaveEnrichmentSection) {
      await onSaveEnrichmentSection('pdp');
    }
  };

  const sectionSummaries = useMemo(() => {
    const toSnippet = (value?: string, fallback?: string) => {
      const trimmed = value?.trim();
      if (!trimmed) {
        return fallback || '';
      }
      return trimmed.length > 120 ? `${trimmed.slice(0, 117)}...` : trimmed;
    };

    const widgetsPayload = normalizeEnrichmentPayload(widgets);
    const visualSearchPayload = normalizeEnrichmentPayload(visualSearch);
    return {
      occasion:
        [occasionName, subOccasionName].filter(Boolean).join(' • ') ||
        'Set the occasion and sub-occasion for this look.',
      look_name: toSnippet(lookName, 'Name the look the way it should appear downstream.'),
      website: toSnippet(
        showMetadata
          ? [lookHook, stylistNote, blurb].filter(Boolean).join(' ')
          : [description, sceneDescription].filter(Boolean).join(' '),
        'Write the core descriptive content for this look.',
      ),
      pdp:
        recommendations.length > 0
          ? 'Review mapped products and recommendation copy.'
          : 'Add recommendation groups and stamped products.',
      search_seeds:
        searchSeedItems.length > 0
          ? 'Review search-seed inputs and generated results.'
          : 'Define search inputs and filters.',
      visual_search:
        visualSearchPayload.sourceItems.length > 0 || widgetsPayload.status
          ? 'Review source images and curate matched products.'
          : 'Generate source images, search products, and curate visual-search widgets.',
    } satisfies Record<LookContentEditorSection, string>;
  }, [
    blurb,
    description,
    lookHook,
    lookName,
    occasionName,
    recommendations.length,
    sceneDescription,
    searchSeedItems.length,
    showMetadata,
    stylistNote,
    subOccasionName,
    visualSearch,
    widgets,
  ]);

  useEffect(() => {
    setActiveSection(orderedSections[0]);
  }, [orderedSections]);

  useEffect(() => {
    const root = scrollContainer;
    if (!root || orderedSections.length === 0) {
      return;
    }

    let frame = 0;

    const updateActiveSection = () => {
      const rootTop = root.getBoundingClientRect().top + 112;
      let nextActiveSection = orderedSections[0];

      orderedSections.forEach(currentSection => {
        const element = sectionRefs.current[currentSection];
        if (!element) {
          return;
        }

        const rect = element.getBoundingClientRect();
        if (rect.top <= rootTop) {
          nextActiveSection = currentSection;
        }
      });

      setActiveSection(previous => (previous === nextActiveSection ? previous : nextActiveSection));
    };

    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateActiveSection);
    };

    updateActiveSection();
    root.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      cancelAnimationFrame(frame);
      root.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [orderedSections, scrollContainer]);

  const sectionTitles: Record<LookContentEditorSection, string> = {
    occasion: 'Occasion',
    look_name: 'Look Name',
    website: 'Website Content',
    pdp: 'Product Recommendations',
    search_seeds: 'Search Seeds',
    visual_search: 'Visual Search',
  };

  const isEnrichmentSection = (currentSection: LookContentEditorSection) =>
    LOOK_CONTENT_EDITOR_ENRICHMENT_SECTIONS.includes(currentSection);

  const widgetsPayload = useMemo(() => normalizeEnrichmentPayload(widgets), [widgets]);
  const visualSearchPayload = useMemo(
    () => normalizeEnrichmentPayload(visualSearch),
    [visualSearch],
  );

  const getEnrichmentCardConfig = (currentSection: LookContentEditorSection) => {
    switch (currentSection) {
      case 'pdp': {
        return {
          actionLabel: 'Manage Recommendations',
          error: undefined,
        };
      }
      case 'visual_search': {
        const hasGeneratedSourceImages =
          visualSearchPayload.sourceItems.some(item => Boolean(item.isolatedImageUrl)) ||
          visualSearchPayload.sourceItems.some(
            item =>
              item.mappedLkStyles.length > 0 || Object.keys(item.mappedProductImages).length > 0,
          );

        return {
          actionDisabled: !hasGeneratedSourceImages && (!isPublished || !onTriggerVisualSearch),
          actionLabel: hasGeneratedSourceImages ? 'Manage Matches' : 'Generate Source Images',
          actionLoading: !hasGeneratedSourceImages ? isTriggeringVisualSearch : false,
          error: widgetsPayload.buildError,
          onAction: hasGeneratedSourceImages
            ? () => setActiveEnrichmentSection('visual_search')
            : () => onTriggerVisualSearch?.(),
        };
      }
      default:
        return null;
    }
  };

  const renderEditorSection = (
    currentSection: LookContentEditorSection,
    showHeading: boolean,
  ): React.ReactNode => {
    switch (currentSection) {
      case 'occasion':
        return <OccasionSection key="occasion" showHeading={showHeading} />;
      case 'look_name':
        return showMetadata ? <LookNameSection key="look_name" showHeading={showHeading} /> : null;
      case 'website':
        return (
          <WebsiteContentSection
            key="website"
            showHeading={showHeading}
            showMetadata={showMetadata}
            isSectionMode={isSectionMode}
          />
        );
      case 'pdp':
        return (
          <PdpContentSection
            key="pdp"
            showHeading={showHeading}
            showMetadata={showMetadata}
            recommendations={recommendations}
            onUpdateRecommendation={updateRecommendation}
            onAddRecommendation={addRecommendation}
            onRemoveRecommendation={removeRecommendation}
            onRemoveMappedProduct={removeMappedProduct}
            onOpenProductPicker={openRecommendationProductPicker}
          />
        );
      case 'search_seeds':
        return showMetadata ? (
          <SearchSeedsSection
            key="search_seeds"
            showHeading={showHeading}
            lookId={lookId}
            searchSeeds={searchSeedItems}
            baseProductColor={baseProductColor}
            onSearchSeedsUpdated={onSearchSeedsUpdated}
          />
        ) : null;
      case 'visual_search':
        return showMetadata ? (
          <VisualSearchSection
            key="visual_search"
            showHeading={showHeading}
            lookId={lookId}
            visualSearch={visualSearch}
            widgets={widgets}
            isPublished={isPublished}
            isTriggeringVisualSearch={isTriggeringVisualSearch}
            onTriggerVisualSearch={onTriggerVisualSearch}
            onRefreshVisualSearch={onRefreshVisualSearch}
            onVisualSearchUpdated={onVisualSearchUpdated}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-2">
      {orderedSections.map((currentSection, sectionIndex) => {
        const isCurrentSection = activeSection === currentSection;
        const sectionContent = renderEditorSection(currentSection, false);

        if (!sectionContent) {
          return null;
        }

        if (compactEnrichments && !isSectionMode && isEnrichmentSection(currentSection)) {
          if (currentSection === 'visual_search' || currentSection === 'search_seeds') {
            return (
              <section
                key={currentSection}
                className="space-y-3 rounded-sm border border-border/50 bg-background p-3"
              >
                {renderEditorSection(currentSection, true)}
              </section>
            );
          }

          const cardConfig = getEnrichmentCardConfig(currentSection);

          if (!cardConfig) {
            return null;
          }

          const actionHandler =
            'onAction' in cardConfig && cardConfig.onAction
              ? cardConfig.onAction
              : () => setActiveEnrichmentSection(currentSection);

          if (currentSection === 'pdp') {
            return (
              <section
                key={currentSection}
                className="space-y-3 rounded-sm border border-border/50 bg-background p-3"
              >
                <ProductRecommendationsSection
                  showHeading={true}
                  presentation="summary"
                  actionLabel={cardConfig.actionLabel}
                  actionDisabled={cardConfig.actionDisabled}
                  actionLoading={cardConfig.actionLoading}
                  onAction={actionHandler}
                  recommendations={recommendations}
                  onUpdateRecommendation={updateRecommendation}
                  onAddRecommendation={addRecommendation}
                  onRemoveRecommendation={removeRecommendation}
                  onRemoveMappedProduct={removeMappedProduct}
                  onOpenProductPicker={openRecommendationProductPicker}
                />
                {cardConfig.error ? (
                  <p className="m-0 text-xs text-rose-700">{cardConfig.error}</p>
                ) : null}
              </section>
            );
          }

          return null;
        }

        if (presentation === 'flat') {
          return (
            <section
              key={currentSection}
              ref={element => {
                sectionRefs.current[currentSection] = element;
              }}
            >
              {/* <header className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="m-0 text-[13px] font-semibold tracking-[0.12em] text-foreground uppercase">
                    {sectionTitles[currentSection]}
                  </h3>
                  {isCurrentSection ? (
                    <span className="text-[10px] font-medium tracking-[0.12em] text-muted-foreground uppercase">
                      Current
                    </span>
                  ) : null}
                </div>
                <p className="m-0 max-w-3xl text-sm leading-6 text-muted-foreground">
                  {sectionSummaries[currentSection]}
                </p>
              </header> */}

              {sectionContent}
            </section>
          );
        }

        return (
          <section
            key={currentSection}
            ref={element => {
              sectionRefs.current[currentSection] = element;
            }}
            className="grid scroll-mt-[calc(var(--look-group-row-top)+1rem)] grid-cols-[22px_minmax(0,1fr)] gap-x-4"
          >
            <div className="relative">
              {sectionIndex !== orderedSections.length - 1 ? (
                <span className="absolute bottom-0 left-1/2 top-7 w-px -translate-x-1/2 bg-border/70" />
              ) : null}

              <div className="sticky top-[var(--look-group-row-top)] z-20 flex h-full justify-center bg-background pt-3">
                <div className="relative h-[22px] w-[22px]">
                  <span
                    className={[
                      'absolute left-0 top-0 flex h-[22px] w-[22px] items-center justify-center rounded-full border transition-colors',
                      isCurrentSection
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-background text-foreground',
                    ].join(' ')}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3 pb-1">
              <header className="sticky top-(--look-group-row-top) z-20 space-y-1.5 border-b border-border/50 bg-background py-3 pr-4">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="m-0 text-[13px] font-semibold tracking-[0.12em] text-foreground uppercase">
                    {sectionTitles[currentSection]}
                  </h3>
                  {isCurrentSection ? (
                    <span className="text-[10px] font-medium tracking-[0.12em] text-muted-foreground uppercase">
                      Current
                    </span>
                  ) : null}
                </div>
                <p className="m-0 max-w-3xl text-sm leading-6 text-muted-foreground">
                  {sectionSummaries[currentSection]}
                </p>
              </header>

              <div className="rounded-sm border border-border/50 bg-background px-4 py-4">
                {sectionContent}
              </div>
            </div>
          </section>
        );
      })}

      {recommendationPickerState.open ? (
        <AddProductsModal
          isOpen={recommendationPickerState.open}
          onClose={closeRecommendationProductPicker}
          selectedProductType={recommendationPickerState.productType || 'Products'}
          portalContainer={productPickerPortalContainer}
          mode="selection"
          currentSelectedIds={
            recommendationPickerState.recommendationIndex !== null
              ? recommendations[recommendationPickerState.recommendationIndex]?.mapped_lk_styles ||
                []
              : []
          }
          currentMappedIds={
            recommendationPickerState.recommendationIndex !== null
              ? recommendations[recommendationPickerState.recommendationIndex]?.mapped_lk_styles ||
                []
              : []
          }
          currentMappedImages={
            recommendationPickerState.recommendationIndex !== null
              ? recommendations[recommendationPickerState.recommendationIndex]
                  ?.mapped_product_images || {}
              : {}
          }
          onSelectionSave={async (
            selectedIds: Array<string>,
            selectedProductImages?: Record<string, string>,
          ) => {
            if (recommendationPickerState.recommendationIndex === null) {
              closeRecommendationProductPicker();
              return;
            }

            updateRecommendation(recommendationPickerState.recommendationIndex, current => ({
              ...current,
              mapped_lk_styles: selectedIds,
              mapped_product_images: selectedProductImages || {},
            }));
            if (onSaveEnrichmentSection) {
              await onSaveEnrichmentSection('pdp');
            }
          }}
        />
      ) : null}

      {compactEnrichments && activeEnrichmentSection ? (
        <EnrichmentSectionDialog
          open={Boolean(activeEnrichmentSection)}
          title={sectionTitles[activeEnrichmentSection]}
          portalContainer={productPickerPortalContainer}
          section={activeEnrichmentSection}
          onClose={() => setActiveEnrichmentSection(null)}
          onReset={
            onResetEnrichmentSection
              ? () => onResetEnrichmentSection(activeEnrichmentSection)
              : undefined
          }
          onSave={
            onSaveEnrichmentSection
              ? async () => {
                  const result = await onSaveEnrichmentSection(activeEnrichmentSection);
                  if (result !== false) {
                    setActiveEnrichmentSection(null);
                  }
                }
              : undefined
          }
          isSaving={isSavingEnrichmentSection}
        >
          <div className="space-y-3 rounded-sm border border-border/50 bg-background">
            {renderEditorSection(activeEnrichmentSection, false)}
          </div>
        </EnrichmentSectionDialog>
      ) : null}
    </div>
  );
};

export type { LookContentEditorSection, LookContentFormValues } from './types';
export default LookContentEditor;
